/**
 * Desktop settings sidebar navigation.
 *
 * Replaces the primary app sidebar (SideNavigation) when the user is on
 * a settings page. Renders in the same fixed-left position and manages
 * the --sidebar-width CSS variable so dashboard content padding adapts.
 *
 * Shows a back arrow + "Settings" header that navigates back to the page
 * the user was on before entering settings, or org home as fallback.
 * Only renders on desktop (lg+); tablet and mobile use the inline sidebar
 * or full-screen list in SettingsLayout.
 */

import { ChevronLeft } from '@untitledui/icons';
import { useLayoutEffect } from 'react';
import { Link, useParams } from 'react-router-dom';

import { SidebarUserFooter } from '@/components/navigation/SidebarUserFooter';
import { EXPANDED_WIDTH } from '@/lib/application/app-navigation/sidebar-navigation-constants';
import { NavSection } from '@/lib/base/nav';

import { SETTINGS_NAV_GROUPS } from './settings-nav-config';
import { SettingsNavGroupHeader, SettingsNavItem } from './settings-nav-item';
import { useSettingsGate } from './use-settings-gate';

interface SettingsSideNavigationProps {
  /** Path to navigate back to. Falls back to org home when null. */
  backPath: string | null;
}

export function SettingsSideNavigation({
  backPath,
}: SettingsSideNavigationProps) {
  const { orgId } = useParams();
  const { isGateVisible } = useSettingsGate();

  // Manage the sidebar width CSS variable (always expanded, no collapse).
  useLayoutEffect(() => {
    document.documentElement.style.setProperty(
      '--sidebar-width',
      `${EXPANDED_WIDTH}px`
    );
    return () => {
      document.documentElement.style.removeProperty('--sidebar-width');
    };
  }, []);

  if (!orgId) {
    return null;
  }

  return (
    <aside
      style={{ width: EXPANDED_WIDTH }}
      className='hidden lg:flex flex-col h-full fixed left-0 top-0 z-50 bg-primary border-r border-secondary'
    >
      <div className='flex flex-col h-full'>
        {/* Back header */}
        <div className='flex items-center gap-1.5 h-[52px] mt-3 px-4'>
          <Link
            to={backPath ?? `/${orgId}`}
            aria-label='Back to main navigation'
            className='flex items-center justify-center size-7 -ml-1 rounded-md text-fg-quaternary transition-colors duration-100 ease-linear hover:bg-primary_hover hover:text-fg-secondary_hover focus-visible:outline-2 focus-visible:outline-offset-2 outline-focus-ring'
          >
            <ChevronLeft className='size-4' />
          </Link>
          <h2 className='text-lg font-semibold text-fg-primary'>Settings</h2>
        </div>

        {/* Settings navigation groups */}
        <nav
          aria-label='Settings sections'
          className='flex-1 overflow-y-auto py-2'
        >
          {SETTINGS_NAV_GROUPS.map(group => {
            const visibleItems = group.items.filter(item =>
              isGateVisible(item.gate)
            );

            if (visibleItems.length === 0) {
              return null;
            }

            return (
              <NavSection key={group.header}>
                <li>
                  <SettingsNavGroupHeader label={group.header} />
                </li>
                {visibleItems.map(item => (
                  <li key={item.id}>
                    <SettingsNavItem item={item} />
                  </li>
                ))}
              </NavSection>
            );
          })}
        </nav>

        {/* User account card — mirrors the main side nav so the footer is
            consistent across the two sidebars. */}
        <div className='px-4 pb-4 pt-2'>
          <SidebarUserFooter />
        </div>
      </div>
    </aside>
  );
}
